import RuleManager from "hello-mono-sdk/packages/web-3d/build/types/lib/RuleManager";
import { RuleJsonType } from "hello-mono-sdk/packages/core";
import "./StepsFlow.scss";
import { useCallback, useEffect, useRef, useState } from "react";
import { BiChevronLeftCircle } from "react-icons/bi";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { IBackground } from "../../types/backend";

export interface StepsFlowProps {
  stepsData: {
    name?: string;
    steps: {
      id: string;
      description?: string;
      title?: string;
      uiType?: "presentation" | "wizard";
      position?: "left" | "center" | "right";
      titleColor?: string;
      titleFontSize?: string;
      descriptionColor?: string;
      descriptionFontSize?: string;
      actions?: {
        parameters: { [key: string]: any };
        type: string;
        id: string;
      }[];
    }[];
  };
  onCompleted: () => void;
  ruleManager: RuleManager;
  bgData?: IBackground;
}
const StepsFlow: React.FC<StepsFlowProps> = ({
  stepsData,
  ruleManager,
  bgData,
  onCompleted,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRunningActions, setIsRunningActions] = useState(false);
  const areFirstStepActionsStarted = useRef<boolean>(false);
  const title = stepsData?.steps?.[currentIndex]?.title;
  const description = stepsData?.steps?.[currentIndex]?.description;
  const totalSteps = stepsData.steps.length;

  useEffect(() => {
    if (areFirstStepActionsStarted.current) {
      return;
    }
    if (isRunningActions) {
      return;
    }
    if (currentIndex === 0) {
      setIsRunningActions(true);
      const customRule: RuleJsonType = {
        id: "id",
        iconText: "iconText",
        type: "Manual",
        isActive: true,
        parameters: {
          node: [],
        },
        actions: stepsData?.steps?.[0]?.actions ?? [],
      };

      areFirstStepActionsStarted.current = true;
      ruleManager.runCustomRule(customRule, () => {
        setIsRunningActions(false);
      });
    }
  }, [currentIndex, isRunningActions, JSON.stringify(stepsData)]);

  const onButtonClick = (type: "PREV" | "NEXT") => {
    if (isRunningActions) {
      return;
    }
    let newIndex = currentIndex;
    if (type === "PREV") {
      newIndex = currentIndex - 1;
    }
    if (type === "NEXT") {
      newIndex = currentIndex + 1;
    }
    setIsRunningActions(true);
    const customRule: RuleJsonType = {
      id: "id",
      iconText: "iconText",
      type: "Manual",
      isActive: true,
      parameters: {
        node: [],
      },
      actions: stepsData?.steps?.[newIndex]?.actions ?? [],
    };
    ruleManager.runCustomRule(customRule, () => {
      setIsRunningActions(false);
    });

    setCurrentIndex(newIndex);
    if (newIndex === totalSteps - 1) {
      onCompleted();
    }
  };

  const onClose = () => {
    let newIndex = totalSteps - 1;
    setIsRunningActions(true);
    const customRule: RuleJsonType = {
      id: "id",
      iconText: "iconText",
      type: "Manual",
      isActive: true,
      parameters: {
        node: [],
      },
      actions: stepsData?.steps?.[newIndex]?.actions ?? [],
    };
    ruleManager.runCustomRule(customRule, () => {
      setIsRunningActions(false);
    });
    onCompleted && onCompleted();
  };

  const findPresentationData = useCallback(() => {
    const mode = stepsData?.steps?.[currentIndex].uiType ?? "wizard";
    const placement = stepsData?.steps?.[currentIndex].position ?? "left";
    const titleFontSize =
      stepsData?.steps?.[currentIndex].titleFontSize ?? "48px";
    const titleColor = stepsData?.steps?.[currentIndex].titleColor ?? "black";
    const descriptionFontSize =
      stepsData?.steps?.[currentIndex].descriptionFontSize ?? "16px";
    const descriptionColor =
      stepsData?.steps?.[currentIndex].descriptionColor ?? "black";

    const placementClass = `${placement}-align`;

    return {
      mode,
      placementClass,
      titleFontSize,
      titleColor,
      descriptionFontSize,
      descriptionColor,
    };
  }, [JSON.stringify(stepsData), currentIndex]);

  const {
    mode,
    placementClass,
    titleFontSize,
    titleColor,
    descriptionFontSize,
    descriptionColor,
  } = findPresentationData();

  console.log({ descriptionFontSize });
  return (
    <>
      <div className="steps-flow-container">
        {mode === "wizard" && title && (
          <div className="steps-top-bar">
            <p dangerouslySetInnerHTML={{ __html: title }} />
          </div>
        )}

        {mode === "presentation" && title && (
          <div className={`title-container ${placementClass}`}>
            <h1
              style={{
                fontSize: titleFontSize,
                color: titleColor,
              }}
              dangerouslySetInnerHTML={{ __html: title }}
            />
            {description && (
              <p
                style={{
                  fontSize: descriptionFontSize,
                  color: descriptionColor,
                }}
                dangerouslySetInnerHTML={{ __html: description }}
              ></p>
            )}
          </div>
        )}
        <div className="close-icon-container">
          <button onClick={onClose}>
            <BiChevronLeftCircle
              fontSize={32}
              style={{
                fill: bgData?.fontColor?.length ? bgData.fontColor : "black",
              }}
            />
          </button>
        </div>
        <div className="steps-navigation">
          <div className="side-container">
            <div className="icon-container">
              {currentIndex !== 0 && (
                <button
                  onClick={() => {
                    onButtonClick("PREV");
                  }}
                  style={{
                    cursor: isRunningActions ? "not-allowed" : "pointer",
                  }}
                >
                  <BsChevronLeft
                    fontSize={32}
                    style={{
                      fill: bgData?.fontColor?.length
                        ? bgData.fontColor
                        : "black",
                    }}
                  />
                </button>
              )}
            </div>
          </div>
          <div className="side-container">
            <div className="icon-container">
              {currentIndex !== totalSteps - 1 && (
                <button
                  onClick={() => {
                    onButtonClick("NEXT");
                  }}
                  style={{
                    cursor: isRunningActions ? "not-allowed" : "pointer",
                  }}
                >
                  <BsChevronRight
                    fontSize={32}
                    style={{
                      fill: bgData?.fontColor?.length
                        ? bgData.fontColor
                        : "black",
                    }}
                  />
                </button>
              )}
            </div>
          </div>
        </div>
        {mode === "wizard" && description && (
          <div className="steps-bottom-bar">
            <p dangerouslySetInnerHTML={{ __html: description }}></p>
          </div>
        )}
      </div>
    </>
  );
};

export default StepsFlow;
